import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceBetweenPoints, getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type RotatedRectangleDrawingState = {
    p1?: Point,
    p2?: Point,
    heightWidthRatio?: number
}

export type RotatedRectangleDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue,
    point1: Point,
    point2: Point,
    point3: Point,
    point4: Point,
}

class RotatedRectangleDrawingTool extends DrawingBase<any, RotatedRectangleDrawingSettings, RotatedRectangleDrawingState> {
    width = 0
    height = 0
    rotation = 0
    slope = 0
    intercept = 0
    widthUpdated = false
    scaledCoordinates: Record<string, PointCoordinates> = {
        p1: { x: 0, y: 0 },
        p2: { x: 0, y: 0 },
        p3: { x: 0, y: 0 },
        p4: { x: 0, y: 0 },
        p5: { x: 0, y: 0 },
        p6: { x: 0, y: 0 }
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        let { p1, p2 } = this._state
        this.calculateAllPoints()
        this.calculateAttributes()
        if (p1 && p2) {

            const { p1: p1s, p2: p2s, p3: p3s, p4: p4s, p5: p5s, p6: p6s } = this.scaledCoordinates

            ctx.beginPath()

            if (!p3s.x) {
                ctx.moveTo(p1s.x, p1s.y)
                ctx.lineTo(p2s.x, p2s.y)
            } else {
                ctx.moveTo(p3s.x, p3s.y)
                ctx.lineTo(p4s.x, p4s.y)
                ctx.lineTo(p5s.x, p5s.y)
                ctx.lineTo(p6s.x, p6s.y)
                ctx.lineTo(p3s.x, p3s.y)

            }
            ctx.globalAlpha = this._settings.strokeColor.opacity
            ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio
            ctx.strokeStyle = this._settings.strokeColor.color
            ctx.closePath()
            ctx.stroke()

            ctx.fillStyle = this._settings.fillColor.color
            ctx.globalAlpha = this._settings.fillColor.opacity
            ctx.fill()
        }
    };

    calculateAttributes() {
        const { p1, p2, heightWidthRatio } = this._state
        if (!p1 || !p2) return

        const p1x = scaledCoordinate(this.timeToCoordinate(p1.x) || 0, this.horizontalPixelRatio)
        const p2x = scaledCoordinate(this.timeToCoordinate(p2.x) || 0, this.horizontalPixelRatio)
        const p1y = scaledCoordinate(this.priceToCoordinate(p1.y) || 0, this.verticalPixelRatio)
        const p2y = scaledCoordinate(this.priceToCoordinate(p2.y) || 0, this.verticalPixelRatio)

        if (p1x && p1y && p2x && p2y) {
            const { p1: p1s, p2: p2s } = this.scaledCoordinates

            this.slope = (p2s.y - p1s.y) / (p2s.x - p1s.x)
            this.intercept = p1s.y - this.slope * p1s.x

            this.width = getDistanceBetweenPoints(p1x, p2x, p1y, p2y)
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x

            if (this.widthUpdated) {
                this._state.heightWidthRatio = this.height / this.width
                this.widthUpdated = false
            }
            if (heightWidthRatio) {
                this.height = this.width * heightWidthRatio
            }
            this.rotation = Math.atan(this.slope)
            const height = this.height || 0
            const xSign = (p2x > p1x ? 1 : -1) * (this.slope > 0 ? -1 : 1)
            const ySign = p2x > p1x ? 1 : -1
            const p3x = p1x + height * xSign * Math.sin(Math.abs(this.rotation))
            const p3y = p1y + height * ySign * Math.cos(Math.abs(this.rotation))
            const p3price = this.coordinateToPrice((p3y / this.verticalPixelRatio) as Coordinate)
            const p3Time = this.coordinateToTime((p3x / this.horizontalPixelRatio) as Coordinate)
            if (p3price && p3Time) {
                this.scaledCoordinates.p3 = {
                    x: p3x,
                    y: p3y
                }
            }
            const p4x = p2x + height * xSign * Math.sin(Math.abs(this.rotation))
            const p4y = p2y + height * ySign * Math.cos(Math.abs(this.rotation))
            const p4price = this.coordinateToPrice((p4y / this.verticalPixelRatio) as Coordinate)
            const p4Time = this.coordinateToTime((p4x / this.horizontalPixelRatio) as Coordinate)
            if (p4price && p4Time) {
                this.scaledCoordinates.p4 = {
                    x: p4x,
                    y: p4y
                }
            }

            const p5x = p2x - height * xSign * Math.sin(Math.abs(this.rotation))
            const p5y = p2y - height * ySign * Math.cos(Math.abs(this.rotation))
            const p5price = this.coordinateToPrice((p5y / this.verticalPixelRatio) as Coordinate)
            const p5Time = this.coordinateToTime((p5x / this.horizontalPixelRatio) as Coordinate)
            if (p5price && p5Time) {
                this.scaledCoordinates.p5 = {
                    x: p5x,
                    y: p5y
                }
            }

            const p6x = p1x - height * xSign * Math.sin(Math.abs(this.rotation))
            const p6y = p1y - height * ySign * Math.cos(Math.abs(this.rotation))
            const p6price = this.coordinateToPrice((p6y / this.verticalPixelRatio) as Coordinate)
            const p6Time = this.coordinateToTime((p6x / this.horizontalPixelRatio) as Coordinate)

            if (p6price && p6Time) {
                this.scaledCoordinates.p6 = {
                    x: p6x,
                    y: p6y
                }
            }
        }

    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
        } else if (this.lastAddedPoint.id == 'p2') {
            point.id = 'p3'
            const x = scaledCoordinate((this.timeToCoordinate(time) || 0), this.horizontalPixelRatio)
            const y = scaledCoordinate((this.priceToCoordinate(price) || 0), this.verticalPixelRatio)

            if (!x || !y) return
            const height = getDistanceFromLine(x, y, this.slope, this.intercept)
            this._state.heightWidthRatio = height / this.width
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    recalculateHandles = () => {
        this.calculateAllPoints()
    }

    calculateAllPoints() {
        const { p1, p2, heightWidthRatio } = this._state
        if (!p1 || !p2) return

        const p1x = scaledCoordinate(this.timeToCoordinate(p1.x) || 0, this.horizontalPixelRatio)
        const p2x = scaledCoordinate(this.timeToCoordinate(p2.x) || 0, this.horizontalPixelRatio)
        const p1y = scaledCoordinate(this.priceToCoordinate(p1.y) || 0, this.verticalPixelRatio)
        const p2y = scaledCoordinate(this.priceToCoordinate(p2.y) || 0, this.verticalPixelRatio)
        if (p1x && p1y && p2x && p2y) {
            this.scaledCoordinates.p1 = {
                x: p1x,
                y: p1y
            }
            this.scaledCoordinates.p2 = {
                x: p2x,
                y: p2y
            }
            this.width = getDistanceBetweenPoints(p1x, p2x, p1y, p2y)
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x

            if (this.widthUpdated) {
                this._state.heightWidthRatio = this.height / this.width
                this.widthUpdated = false
            }
            if (heightWidthRatio) {
                this.height = this.width * heightWidthRatio
            }
            this.rotation = Math.atan(this.slope)
            const height = this.height || 0
            const xSign = (p2x > p1x ? 1 : -1) * (this.slope > 0 ? -1 : 1)
            const ySign = p2x > p1x ? 1 : -1
            const p3x = p1x + height * xSign * Math.sin(Math.abs(this.rotation))
            const p3y = p1y + height * ySign * Math.cos(Math.abs(this.rotation))
            if (p3x && p3y) {
                this.scaledCoordinates.p3 = {
                    x: p3x,
                    y: p3y
                }
            }
            const p4x = p2x + height * xSign * Math.sin(Math.abs(this.rotation))
            const p4y = p2y + height * ySign * Math.cos(Math.abs(this.rotation))
            if (p4x && p4y) {
                this.scaledCoordinates.p4 = {
                    x: p4x,
                    y: p4y
                }
            }

            const p5x = p2x - height * xSign * Math.sin(Math.abs(this.rotation))
            const p5y = p2y - height * ySign * Math.cos(Math.abs(this.rotation))
            if (p5x && p5y) {
                this.scaledCoordinates.p5 = {
                    x: p5x,
                    y: p5y
                }
            }

            const p6x = p1x - height * xSign * Math.sin(Math.abs(this.rotation))
            const p6y = p1y - height * ySign * Math.cos(Math.abs(this.rotation))
            if (p6x && p6y) {
                this.scaledCoordinates.p6 = {
                    x: p6x,
                    y: p6y
                }
            }

        }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state
        const { p3, p4, p5, p6 } = this.scaledCoordinates
        const horizontalRatio = this.horizontalPixelRatio || 1
        const verticalRatio = this.verticalPixelRatio || 1
        const scaleToMediaCoordinates = (point: PointCoordinates): PointCoordinates => ({
            x: point.x / horizontalRatio,
            y: point.y / verticalRatio
        })

        p1 && handles.push(p1)
        p2 && handles.push(p2)
        p3.x && handles.push({
            id: 'p3',
            x: 0 as Time,
            y: 0,
            coordinates: scaleToMediaCoordinates(p3)
        })
        p4.x && handles.push({
            id: 'p4',
            x: 0 as Time,
            y: 0,
            coordinates: scaleToMediaCoordinates(p4)
        })
        p5.x && handles.push({
            id: 'p5',
            x: 0 as Time,
            y: 0,
            coordinates: scaleToMediaCoordinates(p5)
        })
        p6.x && handles.push({
            id: 'p6',
            x: 0 as Time,
            y: 0,
            coordinates: scaleToMediaCoordinates(p6)
        })

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {

        const { p1, p2, p3, p4, p5, p6 } = this.scaledCoordinates

        if (!p1.x || !p2.x || !p3.x || !p4.x || !p5.x || !p6.x) return null

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const centerX = (p1.x + p3.x) / 2
        const centerY = (p1.y + p3.y) / 2
        const translatedX = scaledX - centerX
        const translatedY = scaledY - centerY
        const cosRotation = Math.cos(-this.rotation)
        const sinRotation = Math.sin(-this.rotation)
        const localX = translatedX * cosRotation - translatedY * sinRotation
        const localY = translatedX * sinRotation + translatedY * cosRotation

        const localPoints = [
            p1, p2, p3, p4, p5, p6
        ].map(point => {
            const px = point.x! - centerX
            const py = point.y! - centerY
            return {
                x: px * cosRotation - py * sinRotation,
                y: px * sinRotation + py * cosRotation
            }
        })
        const minX = Math.min(...localPoints.map(p => p.x))
        const maxX = Math.max(...localPoints.map(p => p.x))
        const minY = Math.min(...localPoints.map(p => p.y))
        const maxY = Math.max(...localPoints.map(p => p.y))
        const margin = 2 * Math.max(this.horizontalPixelRatio || 1, this.verticalPixelRatio || 1)
        if (
            localX >= minX - margin && localX <= maxX + margin &&
            localY >= minY - margin && localY <= maxY + margin
        ) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number, coordinates: PointCoordinates, syncMode = false) => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                this.widthUpdated = true
                break
            case 'p2':
                p2.x = time
                p2.y = price
                this.widthUpdated = true
                break
            default:
                if (syncMode) return
                const scaledX = scaledCoordinate(coordinates.x, this.horizontalPixelRatio)
                const scaledY = scaledCoordinate(coordinates.y, this.verticalPixelRatio)
                const height = getDistanceFromLine(scaledX, scaledY, this.slope, this.intercept)
                this._state.heightWidthRatio = height / this.width
                this.publishSyncStateEvent()
        }
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const min = Math.min(p1.y, p2.y)
        const max = Math.max(p1.y, p2.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<RotatedRectangleDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
        if (settings.point2) {
            this._state.p2 = settings.point2
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<RotatedRectangleDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#4caf4f',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 0.7
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: { color: '#4caf4f', opacity: 1 },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: 'Point 1',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point2',
                    label: 'Point 2',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p2',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): RotatedRectangleDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p2) {
            settings.point2 = this._state.p2
        }
        return settings
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof RotatedRectangleDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name)]
            }
        })

        return miniSettings
    }

    getSyncState = (): Partial<RotatedRectangleDrawingState> => {
        return {
            heightWidthRatio: this._state.heightWidthRatio
        }
    }

    getDrawingType(): DrawingTypes {
        return 'rotatedRectangle'
    }
}

export default RotatedRectangleDrawingTool